import { Nav } from "react-bootstrap";
import { DashCircleDotted, Pen, PlusCircle } from "react-bootstrap-icons";
import "./ControlNavList.css";

/**
 * Props for the ControlNavList component.
 */
export interface ControlNavListProps {
  /**
   * Unique identifier for the navigation list.
   */
  id: string;
  /**
   * Array of items to display in the list.
   */
  list: { id: number; label: string }[];
  /**
   * ID of the currently selected item.
   */
  selected?: number;
  /**
   * Callback invoked when an item is selected.
   */
  setSelected?: (ret: number) => void;
  /**
   * Callback to add a new item to the list.
   */
  addItem?: () => void;
  /**
   * Label text for the add item action.
   */
  addLabel?: string;
  /**
   * Callback to remove an item from the list.
   */
  removeItem?: (ret: number) => void;
  /**
   * Label text for the remove item action.
   */
  removeLabel?: string;
  /**
   * Callback to rename an item in the list.
   */
  renameItem?: (ret: number) => void;
  /**
   * Label text for the rename item action.
   */
  renameLabel?: string;
  /**
   * Custom render function for individual items.
   */
  renderItem?: ({ id, label }: { id: number; label: string }) => React.ReactElement;
}

/**
 * A navigation list component that displays a list of items with optional actions for adding, removing, and renaming items.
 * Renders as a vertical pills navigation with interactive icons for each action.
 *
 * @param props - Component props.
 * @param props.id - Unique identifier for the navigation list
 * @param props.list - Array of items to display in the list
 * @param props.selected - ID of the currently selected item
 * @param props.setSelected - Callback function to handle item selection
 * @param props.addItem - Callback function to add a new item
 * @param props.addLabel - Label text for the add item button (defaults to "Add new")
 * @param props.removeItem - Callback function to remove an item
 * @param props.removeLabel - Label text for the remove item action (defaults to "Remove")
 * @param props.renameItem - Callback function to rename an item
 * @param props.renameLabel - Label text for the rename item action (defaults to "Rename")
 * @param props.renderItem - Custom render function for individual items (defaults to label display)
 * @returns A React functional component rendering a Nav with pills variant
 */
export const ControlNavList = ({
  id,
  list,
  selected,
  setSelected,
  addItem,
  addLabel = "Add new",
  removeItem,
  removeLabel = "Remove",
  renameItem,
  renameLabel = "Rename",
  renderItem = ({ label }) => <>{label}</>,
}: ControlNavListProps): React.ReactElement => {
  return (
    <Nav
      variant="pills"
      className="flex-column"
      id={`${id}`}
    >
      {list.map((l) => (
        <Nav.Link
          key={l.id}
          id={`${id}-link-${l.id}`}
          className={`control-nav-item ${selected === l.id ? "active" : ""}`}
          onClick={() => {
            setSelected?.(l.id);
          }}
        >
          {removeItem && (
            <div className="icon-holder">
              <DashCircleDotted
                id={`${id}-remove-${l.id}`}
                onClick={() => removeItem(l.id)}
                aria-label={`${removeLabel} ${l.label}`}
                style={{
                  color: selected === l.id ? "var(--bs-light)" : "var(--bs-danger)",
                }}
              />
            </div>
          )}
          {renameItem && (
            <div className="icon-holder">
              <Pen
                id={`${id}-rename-${l.id}`}
                onClick={() => renameItem(l.id)}
                aria-label={`${renameLabel} ${l.label}`}
                style={{
                  color: selected === l.id ? "var(--bs-light)" : "var(--bs-dark)",
                }}
              />
            </div>
          )}
          <div className="control-nav-inner">{renderItem(l)}</div>
        </Nav.Link>
      ))}
      {addItem && (
        <Nav.Link
          id={`${id}-add`}
          className="control-nav-item"
          onClick={() => addItem()}
        >
          {" "}
          <div className="icon-holder">
            <PlusCircle />
          </div>
          {addLabel}
        </Nav.Link>
      )}
    </Nav>
  );
};

ControlNavList.displayName = "ControlNavList";
